import { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import type { Event } from '@/mocks/admin';

type BackendEvent = {
  id: number;
  titre: string;
  description?: string | null;
  date: string;
  heure: string;
  date_fin?: string | null;
  heure_fin?: string | null;
  lieu: string;
  diocese?: string | null;
  prix?: number | null;
  prix_initial?: number | null;
  admin_profit_fee?: number | null;
  dexpay_payin_fee?: number | null;
  dexpay_payout_fee?: number | null;
  image?: string | null;
  categorie?: string | null;
  organisateur?: string | null;
  contact?: string | null;
  places_disponibles?: number | null;
  status: 'pending' | 'approved' | 'rejected' | 'deleted';
  rejection_reason?: string | null;
  user_id?: number | null;
  created_at?: string;
  ticket_types?: BackendTicketType[];
};

type BackendTicketType = {
  id?: number;
  name: string;
  description?: string | null;
  prix: number;
  prix_initial?: number | null;
  admin_profit_fee?: number | null;
  dexpay_payin_fee?: number | null;
  dexpay_payout_fee?: number | null;
  places_disponibles?: number | null;
};

const fallbackImage = 'https://readdy.ai/api/search-image?query=event%20celebration%20festival%20stage%20lights%20crowd%20music%20africa%20colorful%20vibrant%20atmosphere%20night&width=400&height=250&seq=fsn-event-fallback&orientation=landscape';

type EventInput = Partial<Event> & {
  imageFile?: File | null;
};

const normalizeImageUrl = (value?: string | null) => {
  if (!value) {
    return fallbackImage;
  }

  const normalized = value.trim();

  if (
    normalized.startsWith('blob:') ||
    normalized.startsWith('data:') ||
    normalized.startsWith('file:')
  ) {
    return fallbackImage;
  }

  return normalized;
};

const imageForPayload = (value?: string | null) => {
  if (!value) return undefined;

  const normalized = value.trim();

  if (
    normalized === fallbackImage ||
    normalized.length > 255 ||
    normalized.includes('readdy.ai/api/search-image') ||
    normalized.startsWith('blob:') ||
    normalized.startsWith('data:') ||
    normalized.startsWith('file:')
  ) {
    return undefined;
  }

  return normalized;
};

const combineDateAndTime = (date: string, time: string) => {
  const normalizedTime = time?.slice(0, 5) || '00:00';
  return `${date}T${normalizedTime}:00`;
};

export const mapEvent = (event: BackendEvent): Event => ({
  id: String(event.id),
  title: event.titre,
  type: event.categorie ?? 'Autre',
  date: combineDateAndTime(event.date, event.heure),
  endDate: combineDateAndTime(event.date_fin ?? event.date, event.heure_fin ?? event.heure),
  location: event.lieu,
  diocese: event.diocese ?? '',
  description: event.description ?? '',
  ticketPrice: Number(event.prix ?? 0),
  originalTicketPrice: event.prix_initial == null ? undefined : Number(event.prix_initial),
  adminProfitFee: event.admin_profit_fee == null ? undefined : Number(event.admin_profit_fee),
  dexpayPayinFee: event.dexpay_payin_fee == null ? undefined : Number(event.dexpay_payin_fee),
  dexpayPayoutFee: event.dexpay_payout_fee == null ? undefined : Number(event.dexpay_payout_fee),
  totalSeats: Number(event.places_disponibles ?? 0),
  soldSeats: 0,
  status: event.status,
  organizerId: String(event.user_id ?? ''),
  organizerName: event.organisateur ?? 'Organisateur',
  image: normalizeImageUrl(event.image),
  createdAt: event.created_at ?? new Date().toISOString(),
  contact: event.contact ?? '',
  rejectionReason: event.rejection_reason ?? undefined,
  ticketTypes: (event.ticket_types ?? []).map((ticketType) => ({
    id: ticketType.id ? String(ticketType.id) : undefined,
    name: ticketType.name,
    description: ticketType.description ?? '',
    price: Number(ticketType.prix ?? 0),
    originalPrice: ticketType.prix_initial == null ? undefined : Number(ticketType.prix_initial),
    adminProfitFee: ticketType.admin_profit_fee == null ? undefined : Number(ticketType.admin_profit_fee),
    dexpayPayinFee: ticketType.dexpay_payin_fee == null ? undefined : Number(ticketType.dexpay_payin_fee),
    dexpayPayoutFee: ticketType.dexpay_payout_fee == null ? undefined : Number(ticketType.dexpay_payout_fee),
    seats: ticketType.places_disponibles == null ? null : Number(ticketType.places_disponibles),
  })),
});

const formatApiDate = (value: string) => {
  if (!value) return '';

  return value.includes('T')
    ? value.split('T')[0]
    : new Date(value).toISOString().slice(0, 10);
};

const formatApiTime = (value: string) => {
  if (!value) return '00:00';

  if (value.includes('T')) {
    return value.split('T')[1]?.slice(0, 5) || '00:00';
  }

  return new Date(value).toISOString().slice(11, 16);
};

const buildPayload = (event: Event | EventInput) => {
  const imageFile = 'imageFile' in event ? event.imageFile : null;

  if (imageFile instanceof File) {
    const formData = new FormData();

    if (event.title !== undefined) formData.append('titre', event.title);
    if (event.description !== undefined) formData.append('description', event.description);
    if (event.date !== undefined) formData.append('date', formatApiDate(event.date));
    if (event.date !== undefined) formData.append('heure', formatApiTime(event.date));
    if (event.endDate !== undefined) formData.append('date_fin', formatApiDate(event.endDate));
    if (event.endDate !== undefined) formData.append('heure_fin', formatApiTime(event.endDate));
    if (event.location !== undefined) formData.append('lieu', event.location);
    if (event.diocese !== undefined) formData.append('diocese', event.diocese);
    if (event.ticketPrice !== undefined) formData.append('prix', String(event.ticketPrice));
    if (event.type !== undefined) formData.append('categorie', event.type);
    if (event.totalSeats !== undefined) formData.append('places_disponibles', String(event.totalSeats));
    if (event.ticketTypes !== undefined) {
      formData.append('ticket_types', JSON.stringify(event.ticketTypes.map((ticketType) => ({
        id: ticketType.id,
        name: ticketType.name,
        description: ticketType.description,
        prix: ticketType.price,
        places_disponibles: ticketType.seats,
      }))));
    }
    if (event.contact !== undefined) formData.append('contact', event.contact);
    if (event.organizerName !== undefined) formData.append('organisateur', event.organizerName);

    formData.append('image_file', imageFile);

    return formData;
  }

  return JSON.stringify({
    titre: event.title,
    description: event.description,
    date: event.date ? formatApiDate(event.date) : undefined,
    heure: event.date ? formatApiTime(event.date) : undefined,
    date_fin: event.endDate ? formatApiDate(event.endDate) : undefined,
    heure_fin: event.endDate ? formatApiTime(event.endDate) : undefined,
    lieu: event.location,
    diocese: event.diocese,
    prix: event.ticketPrice,
    image: imageForPayload(event.image),
    categorie: event.type,
    places_disponibles: event.totalSeats,
    ticket_types: event.ticketTypes?.map((ticketType) => ({
      id: ticketType.id,
      name: ticketType.name,
      description: ticketType.description,
      prix: ticketType.price,
      places_disponibles: ticketType.seats,
    })),
    contact: event.contact,
    organisateur: event.organizerName,
  });
};

export const useEventsStore = () => {
  const { user, isAuthenticated } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    setLoading(true);

    try {
      let path = '/evenements';

      if (isAuthenticated && user?.role === 'admin') {
        path = '/admin/evenements';
      } else if (isAuthenticated && user?.role === 'organizer') {
        path = '/organizer/evenements';
      }

      const response = await apiRequest<BackendEvent[]>(path);
      setEvents(response.map(mapEvent));
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    void fetchEvents();
  }, [fetchEvents]);

  const addEvent = async (event: EventInput) => {
    await apiRequest('/evenements', {
      method: 'POST',
      body: buildPayload(event),
    });

    await fetchEvents();
  };

  const approveEvent = async (id: string) => {
    await apiRequest(`/admin/evenements/${id}/approve`, {
      method: 'POST',
    });

    await fetchEvents();
  };

  const rejectEvent = async (id: string, reason: string) => {
    await apiRequest(`/admin/evenements/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });

    await fetchEvents();
  };

  const updateEvent = async (id: string, changes: EventInput) => {
    const body = buildPayload(changes);

    if (body instanceof FormData) {
      body.append('_method', 'PUT');
    }

    await apiRequest(`/evenements/${id}`, {
      method: body instanceof FormData ? 'POST' : 'PUT',
      body,
    });

    await fetchEvents();
  };

  const deleteEvent = async (id: string) => {
    await apiRequest(`/evenements/${id}`, {
      method: 'DELETE',
    });

    await fetchEvents();
  };

  const restoreEvent = async (id: string) => {
    await apiRequest(`/evenements/${id}/restore`, {
      method: 'POST',
    });

    await fetchEvents();
  };

  const getOrganizerEvents = (organizerId: string) =>
    events.filter((event) => event.organizerId === organizerId);

  return {
    events,
    loading,
    addEvent,
    approveEvent,
    rejectEvent,
    updateEvent,
    deleteEvent,
    restoreEvent,
    getOrganizerEvents,
    refreshEvents: fetchEvents,
  };
};
